import React, { useCallback, useId, useMemo } from 'react';
import FormWrapper from 'c-components/Forms/FormWrapper';
import { useAPIClientRequest, useAutoResetBoolean, useEntityPage } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import type { Campaign, SaveCampaignPopSettingsPayload } from 'c-sdk';
import { Controller } from 'react-hook-form';
import { Alert, AutoGrid, Button, DateField, SelectField, TextField } from 'c-components';
import { Translate, useCommonTranslation } from 'c-translation';
import { Box, Stack, Typography } from '@mui/material';
import { NetworkRequestState } from 'c-data-layer';
import to from 'await-to-js';
import { useEntityData, useUpdateEntityData } from 'c-data';
import type { UseBoolean } from 'react-hanger';
import ApiClient from 'c-data/apiClient';
import ToggleButtonGroup from 'c-components/Forms/ToggleButtonGroup';
import type { FeatureFlagChoice } from './utils';
import {
    choiceToFeatureFlag,
    featureFlagToChoice,
    overridesBuyerFeatureFlag,
    uniqueCreativeValues,
} from './utils';

import PopSettingsLogSourceIdentifier from './PopSettingsLogSourceIdentifier';
import AudienceVersionPicker from './AudienceVersionPicker';
import type { AudienceVersionId } from './popSettingsPayload';
import {
    impressionMultiplierUpdate,
    initialAudienceVersionIds,
    isImpressionMultiplierInput,
    logSourceNamesToAdd,
    logSourceRowsFromCampaign,
    pcaDateTimeWithDate,
    pcaDateTimeWithTime,
    sanitizeVersionIds,
    splitPcaDateTime,
    withoutUnusedPcaEnd,
} from './popSettingsPayload';

type Props = {
    campaignId: number;
    addNameState: UseBoolean;
};

type FormDataStructure = Partial<
    Omit<SaveCampaignPopSettingsPayload, 'creatives' | 'audience_version_ids'> & {
        creatives: string;
        impression_multiplier: number;
        audience_version_ids: AudienceVersionId[];
    }
>;

/**
 * UL-7904 -- one tri-state campaign feature flag.
 *
 * Inherit is the default and the common case: the buyer decides. The two overrides are offered
 * explicitly because a campaign turning a feature OFF against a buyer that has it on used to be
 * impossible -- the API OR'd the two levels -- and that was indistinguishable from the setting
 * being broken. A warning appears only when the campaign actually disagrees with its buyer.
 */
const FeatureFlagField: React.FC<{
    name: 'energy_efficiency' | 'display_verification';
    label: string;
    buyerValue?: boolean;
}> = ({ name, label, buyerValue }) => {
    const t = useCommonTranslation();

    const options = useMemo(
        () => [
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagInherit'),
                value: 'inherit',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagOn'),
                value: 'on',
            },
            {
                label: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagOff'),
                value: 'off',
            },
        ],
        [t],
    );

    return (
        <Controller
            name={name}
            render={({ field }) => (
                <Stack gap={0.5}>
                    <ToggleButtonGroup
                        {...field}
                        exclusive
                        size="small"
                        label={label}
                        options={options}
                        value={featureFlagToChoice(field.value)}
                        onChange={(choice: FeatureFlagChoice) =>
                            field.onChange(choiceToFeatureFlag(choice))
                        }
                    />
                    <Typography variant="caption" color="text.secondary">
                        {t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagInheritHelper',
                            {
                                buyerState: t(
                                    buyerValue
                                        ? 'Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagOn'
                                        : 'Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagOff',
                                ),
                            },
                        )}
                    </Typography>
                    {overridesBuyerFeatureFlag(field.value, buyerValue) && (
                        <Alert severity="warning" variant="outlined">
                            {t(
                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.featureFlagOverrideWarning',
                            )}
                        </Alert>
                    )}
                </Stack>
            )}
        />
    );
};

const updateSelectedCreatives = (currentValue: string, updatedValues: string[]) => {
    const currentNames = uniqueCreativeValues(currentValue);
    const updatedNamesSet = new Set(updatedValues);

    const retainedNames = currentNames.filter(name => updatedNamesSet.has(name));

    const finalNames = [...new Set([...retainedNames, ...updatedValues])];

    return finalNames.join('\n');
};

const PopSettingsForm: React.FC = () => {
    const measurementProviderId = useId();
    const t = useCommonTranslation();
    const { entity } = useEntityPage<Campaign>();
    return (
        <AutoGrid xs={12} spacing={2} pb={2}>
            <Box width="100%">
                <Controller
                    name="audience_version_ids"
                    render={({ field }) => (
                        <AudienceVersionPicker
                            value={sanitizeVersionIds(field.value)}
                            onChange={ids => field.onChange(sanitizeVersionIds(ids))}
                        />
                    )}
                />
            </Box>
            <Stack gap={2}>
                <FeatureFlagField
                    name="energy_efficiency"
                    label={t(
                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.energyEfficiencyLabel',
                    )}
                    buyerValue={entity?.energy_efficiency_enabled_buyer}
                />
                <FeatureFlagField
                    name="display_verification"
                    label={t(
                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.displayVerificationEfficiencyLabel',
                    )}
                    buyerValue={entity?.display_verification_enabled_buyer}
                />
            </Stack>
            <Controller
                render={({ field }) => (
                    <TextField
                        {...field}
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.impressionMultiplierLabel',
                        )}
                        size="small"
                        onChange={e => {
                            const inputValue = e.target.value;

                            if (isImpressionMultiplierInput(inputValue)) {
                                field.onChange(inputValue);
                            }
                        }}
                    />
                )}
                name="impression_multiplier"
            />
            <Controller
                name="pca_end_setting"
                render={({ field }) => (
                    <SelectField
                        {...field}
                        size="small"
                        options={[
                            {
                                label: t('Modules.Main.Campaigns.CampaignSchedule.popSettings.now'),
                                value: 'now',
                            },
                            {
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.twoDaysAgo',
                                ),
                                value: 'two_days_ago',
                            },
                            {
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.custom',
                                ),
                                value: 'custom',
                            },
                            {
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.BasedOnOwner',
                                ),
                                value: 'based_on_owners',
                            },
                        ]}
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.refreshTypeLabel',
                        )}
                    />
                )}
            />

            <Controller
                name="pca_end_setting"
                render={({ field }) =>
                    field.value === 'custom' && (
                        <Controller
                            name="pca_end_datetime"
                            render={({ field }) => {
                                const { datePart, timePart } = splitPcaDateTime(
                                    field.value as string | null,
                                );

                                return (
                                    <Stack gap={2}>
                                        <DateField
                                            {...field}
                                            label={t(
                                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.refreshDateLabel',
                                            )}
                                            value={datePart}
                                            onChange={date =>
                                                field.onChange(pcaDateTimeWithDate(date, timePart))
                                            }
                                            inputProps={{ size: 'small' }}
                                        />

                                        {datePart && (
                                            <TextField
                                                label={t(
                                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.minTime',
                                                )}
                                                type="time"
                                                size="small"
                                                fullWidth
                                                value={timePart.slice(0, 5)}
                                                onChange={event => {
                                                    if (datePart)
                                                        field.onChange(
                                                            pcaDateTimeWithTime(
                                                                datePart,
                                                                event.target.value,
                                                            ),
                                                        );
                                                }}
                                            />
                                        )}
                                    </Stack>
                                );
                            }}
                        />
                    )
                }
            />

            <Controller
                name="pca_start_datetime"
                render={({ field }) => {
                    const { datePart, timePart } = splitPcaDateTime(field.value);

                    return (
                        <Stack gap={2}>
                            <DateField
                                {...field}
                                label={t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.minDate',
                                )}
                                onChange={date =>
                                    field.onChange(pcaDateTimeWithDate(date, timePart))
                                }
                                value={datePart}
                            />

                            {datePart && (
                                <TextField
                                    label={t(
                                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.minTime',
                                    )}
                                    type="time"
                                    value={timePart.slice(0, 5)}
                                    onChange={event => {
                                        if (datePart) {
                                            field.onChange(
                                                pcaDateTimeWithTime(datePart, event.target.value),
                                            );
                                        }
                                    }}
                                    size="small"
                                    fullWidth
                                />
                            )}
                        </Stack>
                    );
                }}
            />

            <Controller
                name="measurement_providers"
                render={({ field }) => (
                    <SelectField
                        {...field}
                        id={measurementProviderId}
                        size="small"
                        value={field.value ?? 'none'}
                        onChange={event =>
                            field.onChange(
                                event.target.value === 'none' ? null : event.target.value,
                            )
                        }
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.popSettings.measurementProviderLabel',
                        )}
                        options={[
                            {
                                value: 'Reveal Mobile',
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.measurementProviderRevealMobile',
                                ),
                            },
                            {
                                value: 'Kochava',
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.measurementProviderKochava',
                                ),
                            },
                            {
                                value: 'none',
                                label: t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.measurementProviderNone',
                                ),
                            },
                        ]}
                    />
                )}
            />
            <PopSettingsLogSourceIdentifier />
        </AutoGrid>
    );
};
const PopSettingsFormWrapper: React.FC<Props> = ({ campaignId, addNameState }) => {
    const { entity, pageConfig } = useEntityPage<Campaign>();
    const { start: updateParamsStart, error: updateError } = useAPIClientRequest(
        ApiClient.Entities.Campaign.UpdateMediaParameter,
    );
    const { update, getUpdatingById } = useUpdateEntityData<Campaign>('Campaign');

    const initialVersionIds = useMemo<AudienceVersionId[]>(
        () => initialAudienceVersionIds(entity),
        [entity],
    );

    const formOpts = useMemo(
        () => ({
            defaultValues: {
                measurement_providers: entity?.measurement_providers ?? null,
                audience_version_ids: initialVersionIds,
                pca_end_datetime: entity?.pca_end_datetime,
                pca_end_setting: entity?.pca_end_setting,
                // UL-7904 -- the campaign's own value, NOT the effective one. Seeding from the
                // effective value would show every inheriting campaign as an explicit override,
                // and the first save would turn the inheritance into one for real.
                display_verification: entity?.display_verification_enabled_campaign ?? null,
                energy_efficiency: entity?.energy_efficiency_enabled_campaign ?? null,
                impression_multiplier: entity?.impression_multiplier ?? 1,
                pca_start_datetime: entity?.pca_start_datetime,
                log_source_campaign_identifiers: logSourceRowsFromCampaign(
                    entity?.logSourceCampaignIdentifiers,
                ),
            } as FormDataStructure,
        }),
        [entity, initialVersionIds],
    );
    const { start, requestStateRef, error, hasFailed, hasSucceeded, isLoading } =
        useAPIClientRequest(apiClient.Entities.Campaign.updatePopSettings);
    const { upsertEntity } = useEntityData('Campaign');
    const { value: successful, setTrue: setSuccessful } = useAutoResetBoolean(false, 2500);
    const onSubmit = useCallback(
        async (data: FormDataStructure) => {
            if (requestStateRef.current === NetworkRequestState.InProgress) return;

            const payload: FormDataStructure = withoutUnusedPcaEnd(data);

            const namesToAdd = logSourceNamesToAdd(
                payload.log_source_campaign_identifiers,
                logSourceRowsFromCampaign(entity?.logSourceCampaignIdentifiers),
                campaignId,
            );

            if (namesToAdd.length > 0) {
                await to(
                    updateParamsStart(campaignId, { parameter: 'name', identifier: namesToAdd }),
                );
                if (!isLoading && !updateError) addNameState.setTrue();
            }

            const multiplierUpdate = impressionMultiplierUpdate(
                entity.impression_multiplier,
                payload.impression_multiplier,
            );
            if (multiplierUpdate) {
                await to(
                    update(campaignId, {
                        id: campaignId,
                        ...multiplierUpdate,
                    } as unknown as Campaign),
                );
            }

            const [err, succ] = await to(
                start(campaignId, payload as unknown as SaveCampaignPopSettingsPayload, [
                    ...(pageConfig.systemSearch.defaultIncludes ?? []),
                    'audienceVersions',
                ]),
            );

            if (!err && succ?.data?.data?.id === campaignId) {
                upsertEntity(succ.data.data);
                setSuccessful();
            }
        },
        [
            requestStateRef,
            entity,
            start,
            campaignId,
            pageConfig.systemSearch.defaultIncludes,
            updateParamsStart,
            isLoading,
            updateError,
            addNameState,
            update,
            upsertEntity,
            setSuccessful,
        ],
    );

    const t = useCommonTranslation();

    return (
        <FormWrapper<FormDataStructure> onSubmit={onSubmit} formOptions={formOpts}>
            <PopSettingsForm />
            <Stack gap={2}>
                {hasFailed && <Alert severity="error">{String(error)}</Alert>}
                {successful && (
                    <Alert severity="success">
                        {t('Modules.Main.Campaigns.CampaignSchedule.popSettings.saveSuccess')}
                    </Alert>
                )}
                <Button type="submit" disabled={isLoading}>
                    <Translate path="Modules.Main.Campaigns.CampaignSchedule.popSettings.submitBtn" />
                </Button>
                {updateError && <Alert severity="error">{String(updateError)}</Alert>}
            </Stack>
        </FormWrapper>
    );
};

export default PopSettingsFormWrapper;
